/**
 * Accounting > Cash Outlook — the working behind the buyer's "cash looks fine / tight".
 *
 * Greg, 2026-10-08: the buyer is not to see the bank balance, and today's balance is the
 * wrong question anyway. What matters is whether the cash will be there on the day a
 * bill comes due. This page is where the balance is entered and where Greg, Matt Sr and
 * Mercedes can see the projection the buyer's one-line read is built from.
 *
 * Renders what lib/cash-projection.js computed and derives nothing. Limited by name on
 * the server (cash-outlook.js); the sidebar hides it from everybody else as a courtesy.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { cashOutlook } from '../services/api';

const money = (v) => (v == null || !isFinite(Number(v)) ? '—'
  : (Number(v) < 0 ? '-$' : '$') + Math.abs(Math.round(Number(v))).toLocaleString('en-US'));
const fmtDay = (d) => {
  if (!d) return '—';
  const [y, m, day] = String(d).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
const todayLocal = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date());

const STATUS = {
  ok: { bg: '#e8f5ec', fg: '#1c7c43', label: 'Fine' },
  tight: { bg: '#fff3e6', fg: '#9a4a1c', label: 'Tight' },
  short: { bg: '#fdeaea', fg: '#b42318', label: 'Short' },
  unknown: { bg: '#eef4fa', fg: '#00395C', label: 'No read' },
};
const label = { fontSize: 12, fontWeight: 600, marginBottom: 4 };
const th = { textAlign: 'right', padding: '6px 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'var(--gray-500, #6b7280)' };
const td = { textAlign: 'right', padding: '7px 10px', fontSize: 13.5 };

export default function CashOutlook() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [bal, setBal] = useState({ amount: '', asOf: todayLocal() });
  const [cushion, setCushion] = useState('');
  const [tryIt, setTryIt] = useState({ amount: '', dueDate: '', vendor: '' });
  const [read, setRead] = useState(null);
  const [ev, setEv] = useState({ direction: 'out', amount: '', day: '', label: '' });

  const load = useCallback(async () => {
    setErr('');
    try {
      const d = await cashOutlook('get');
      setData(d);
      setCushion(d.cushionSet ? String(Math.round(d.cushion)) : '');
    } catch (e) { setErr(e.message || 'Could not load the cash outlook.'); }
    setLoading(false);
  }, []);

  useEffect(() => {
    // Bring the last few weeks of the bank register in from QuickBooks, then read. A
    // failure to refresh is not a failure to show what is already here.
    cashOutlook('sync').catch(() => {}).then(load);
  }, [load]);

  const run = async (fn) => {
    setBusy(true); setErr('');
    try { await fn(); } catch (e) { setErr(e.message || 'That did not save.'); }
    setBusy(false);
  };
  const saveBalance = () => run(async () => {
    const d = await cashOutlook('set-balance', { amount: bal.amount, asOf: bal.asOf });
    setData(d); setBal({ amount: '', asOf: todayLocal() }); setRead(null);
  });
  const saveCushion = () => run(async () => { setData(await cashOutlook('set-cushion', { cushion })); setRead(null); });
  const weigh = () => run(async () => { setRead(await cashOutlook('weigh', tryIt)); });
  const addEvent = () => run(async () => {
    setData(await cashOutlook('add-event', ev)); setEv({ direction: 'out', amount: '', day: '', label: '' }); setRead(null);
  });
  const removeEvent = (id) => run(async () => { setData(await cashOutlook('remove-event', { id })); setRead(null); });

  if (loading && !data) {
    return <div className="page-content"><div style={{ color: 'var(--gray-500, #6b7280)' }}>Reading the bank register from QuickBooks…</div></div>;
  }

  const p = data || {};
  const pos = p.position;
  const low = p.low;
  const lowTone = !p.usable || !low ? STATUS.unknown : low.balance < 0 ? STATUS.short : low.balance < p.cushion ? STATUS.tight : STATUS.ok;

  return (
    <div className="page-content">
      <div className="breadcrumb"><span>Accounting</span><span> / </span><span>Cash Outlook</span></div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ color: 'var(--navy)', margin: 0 }}>Cash Outlook</h1>
        <button className="btn btn-ghost" onClick={() => { setLoading(true); load(); }} disabled={loading}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>
      <div style={{ fontSize: 13.5, color: 'var(--gray-600, #4b5563)', marginBottom: 14, maxWidth: 780 }}>
        Where the bank balance is headed over the next ten weeks. A buyer planning an order
        is told only whether cash looks fine, tight or short on the day the bill would be
        due. They never see a balance. This page is limited to Greg, Matt and Mercedes.
      </div>

      {err && <div className="card" style={{ padding: 14, color: '#b42318', marginBottom: 12 }}>{err}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, marginBottom: 12 }}>
        <div className="card" style={{ padding: 16 }}>
          <div style={label}>Bank balance</div>
          {pos ? (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--navy, #00395C)' }}>{money(pos.amount)}</div>
              <div style={{ fontSize: 12.5, color: pos.stale ? '#b42318' : 'var(--gray-600, #4b5563)' }}>
                as of {fmtDay(pos.asOf)}{pos.by ? `, entered by ${pos.by.split(' ')[0]}` : ''}
                {pos.stale ? '. Too old to project from; enter a current one.' : ''}
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: '#9a4a1c', marginBottom: 10 }}>
              No balance entered yet. Nothing can be projected, and buyers get no cash read, until one is.
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '1 1 120px' }}>
              <div style={{ fontSize: 11.5, color: 'var(--gray-600, #4b5563)' }}>What the bank shows ($)</div>
              <input className="form-input" inputMode="decimal" value={bal.amount} style={{ width: '100%' }}
                onChange={(e) => setBal({ ...bal, amount: e.target.value })} />
            </div>
            <div>
              <div style={{ fontSize: 11.5, color: 'var(--gray-600, #4b5563)' }}>As of</div>
              <input className="form-input" type="date" value={bal.asOf} max={todayLocal()}
                onChange={(e) => setBal({ ...bal, asOf: e.target.value })} />
            </div>
            <button className="btn btn-primary" onClick={saveBalance} disabled={busy || !bal.amount.trim()}>Save Balance</button>
          </div>
          {p.register && (
            <div style={{ fontSize: 11.5, color: 'var(--gray-500, #6b7280)', marginTop: 8 }}>
              For reference, QuickBooks' own register reads {money(p.register.balance)} as of {fmtDay(p.register.day)}.
              That is the books, not the bank, and it is never used here.
            </div>
          )}
        </div>

        <div className="card" style={{ padding: 16 }}>
          <div style={label}>Lowest point in the next ten weeks</div>
          {p.usable && low ? (
            <>
              <div style={{ fontSize: 24, fontWeight: 700, color: lowTone.fg }}>{money(low.balance)}</div>
              <div style={{ fontSize: 12.5, color: 'var(--gray-600, #4b5563)' }}>around {fmtDay(low.day)}</div>
            </>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--gray-600, #4b5563)' }}>{p.whyNot || 'No projection yet.'}</div>
          )}
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 11.5, color: 'var(--gray-600, #4b5563)' }}>
              Cushion: below this a buyer is told cash is tight ($)
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input className="form-input" inputMode="decimal" value={cushion} style={{ width: 140 }}
                placeholder={p.weeklyOut ? String(Math.round(p.weeklyOut)) : ''}
                onChange={(e) => setCushion(e.target.value)} />
              <button className="btn btn-outline" onClick={saveCushion} disabled={busy}>Save</button>
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--gray-500, #6b7280)', marginTop: 4 }}>
              {p.cushionSet ? 'Set by hand. Empty it to go back to one week of normal spending.'
                : `Not set, so it is one week of normal spending (${money(p.weeklyOut)}).`}
            </div>
          </div>
        </div>

        <div className="card" style={{ padding: 16 }}>
          <div style={label}>Bills already in CPx, not yet paid</div>
          <div style={{ fontSize: 13.5, lineHeight: 1.7 }}>
            <div>Approved, ready to pay: <strong>{money(p.queue && p.queue.approved.amount)}</strong> ({p.queue ? p.queue.approved.n : 0} bills)</div>
            <div>Waiting on approval: <strong>{money(p.queue && p.queue.pending.amount)}</strong> ({p.queue ? p.queue.pending.n : 0} bills)</div>
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--gray-500, #6b7280)', marginTop: 6 }}>
            Shown for context. They are already inside "money out" below, which is everything
            a normal week brings, so they are not taken off a second time.
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 12 }}>
        <div style={label}>Known one-off money coming in or going out</div>
        <div style={{ fontSize: 12.5, color: 'var(--gray-600, #4b5563)', marginBottom: 8, maxWidth: 780 }}>
          The projection is built from last year's ordinary days, so it cannot know about a loan draw,
          an insurance premium, a tax payment or a big one-time bill. List them here and they are
          counted on their day. Regular payroll, crab bills and card payments are already in it.
        </div>
        {(p.events || []).length === 0 && (
          <div style={{ fontSize: 13, color: 'var(--gray-500, #6b7280)', marginBottom: 8 }}>Nothing listed.</div>
        )}
        {(p.events || []).map((e) => (
          <div key={e.id} style={{ display: 'flex', gap: 10, alignItems: 'baseline', fontSize: 13.5, padding: '3px 0' }}>
            <span style={{ width: 70 }}>{fmtDay(e.day)}</span>
            <span style={{ width: 110, textAlign: 'right', fontWeight: 600, color: e.amount < 0 ? '#b42318' : '#1c7c43' }}>
              {e.amount < 0 ? 'out ' : 'in '}{money(Math.abs(e.amount))}
            </span>
            <span style={{ flex: 1 }}>{e.label}{e.by ? <span style={{ fontSize: 11.5, color: 'var(--gray-500, #6b7280)' }}> · {e.by.split(' ')[0]}</span> : null}</span>
            <button className="btn btn-ghost" style={{ padding: 0 }} disabled={busy} onClick={() => removeEvent(e.id)}>Remove</button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 8 }}>
          <select className="form-input" value={ev.direction} onChange={(e) => setEv({ ...ev, direction: e.target.value })}>
            <option value="out">Going out</option>
            <option value="in">Coming in</option>
          </select>
          <div>
            <div style={{ fontSize: 11.5, color: 'var(--gray-600, #4b5563)' }}>Amount ($)</div>
            <input className="form-input" inputMode="decimal" value={ev.amount} style={{ width: 120 }}
              onChange={(e) => setEv({ ...ev, amount: e.target.value })} />
          </div>
          <div>
            <div style={{ fontSize: 11.5, color: 'var(--gray-600, #4b5563)' }}>On</div>
            <input className="form-input" type="date" value={ev.day} min={todayLocal()}
              onChange={(e) => setEv({ ...ev, day: e.target.value })} />
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <div style={{ fontSize: 11.5, color: 'var(--gray-600, #4b5563)' }}>What it is</div>
            <input className="form-input" value={ev.label} style={{ width: '100%' }}
              placeholder="e.g. Liability insurance premium"
              onChange={(e) => setEv({ ...ev, label: e.target.value })} />
          </div>
          <button className="btn btn-outline" onClick={addEvent} disabled={busy || !ev.amount || !ev.day || !ev.label.trim()}>Add</button>
        </div>
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 12 }}>
        <div style={label}>Try a purchase: what the buyer would be told</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: 11.5, color: 'var(--gray-600, #4b5563)' }}>Order amount ($)</div>
            <input className="form-input" inputMode="decimal" value={tryIt.amount} style={{ width: 130 }}
              onChange={(e) => setTryIt({ ...tryIt, amount: e.target.value.replace(/[$,\s]/g, '') })} />
          </div>
          <div>
            <div style={{ fontSize: 11.5, color: 'var(--gray-600, #4b5563)' }}>Bill due</div>
            <input className="form-input" type="date" value={tryIt.dueDate} min={todayLocal()}
              onChange={(e) => setTryIt({ ...tryIt, dueDate: e.target.value })} />
          </div>
          <div style={{ flex: '1 1 180px' }}>
            <div style={{ fontSize: 11.5, color: 'var(--gray-600, #4b5563)' }}>Vendor (optional)</div>
            <input className="form-input" value={tryIt.vendor} style={{ width: '100%' }}
              onChange={(e) => setTryIt({ ...tryIt, vendor: e.target.value })} />
          </div>
          <button className="btn btn-navy" onClick={weigh} disabled={busy || !tryIt.amount}>Check</button>
        </div>
        {read && (
          <div style={{ marginTop: 12 }}>
            <div style={{
              background: (STATUS[read.status] || STATUS.unknown).bg, color: (STATUS[read.status] || STATUS.unknown).fg,
              borderRadius: 6, padding: '10px 12px', fontSize: 14,
            }}>
              <strong>{(STATUS[read.status] || STATUS.unknown).label}.</strong> {read.sentence}
            </div>
            {read.working && (
              <div style={{ fontSize: 12.5, color: 'var(--gray-600, #4b5563)', marginTop: 8, lineHeight: 1.6 }}>
                {read.working.reason ? read.working.reason : (
                  <>
                    Behind that, for you only: we would normally spend {money(read.working.ordinaryWithVendor)} with
                    them by {fmtDay(read.working.due)}, so {money(read.working.extra)} of this order is extra.
                    With it, the lowest balance in the week after the due date is {money(read.working.lowBalance)} on {fmtDay(read.working.lowDay)},
                    against a cushion of {money(read.working.cushion)}.
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 12, overflowX: 'auto' }}>
        <div style={label}>Week by week</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--gray-200, #e5e7eb)' }}>
              <th style={{ ...th, textAlign: 'left' }}>Week of</th>
              <th style={th}>Money in</th>
              <th style={th}>Money out</th>
              <th style={th}>Net</th>
              <th style={th}>Lowest that week</th>
              <th style={th}>End of week</th>
            </tr>
          </thead>
          <tbody>
            {(p.weeks || []).map((w) => {
              const tone = w.low == null ? null : w.low < 0 ? STATUS.short : w.low < p.cushion ? STATUS.tight : null;
              return (
                <tr key={w.weekOf} style={{ borderBottom: '1px solid var(--gray-100, #f3f4f6)', background: tone ? tone.bg : 'transparent' }}>
                  <td style={{ ...td, textAlign: 'left' }}>{fmtDay(w.weekOf)}</td>
                  <td style={td}>{money(w.in)}</td>
                  <td style={td}>{money(w.out)}</td>
                  <td style={{ ...td, color: w.in - w.out < 0 ? '#b42318' : '#1c7c43' }}>{money(w.in - w.out)}</td>
                  <td style={{ ...td, fontWeight: tone ? 700 : 400, color: tone ? tone.fg : 'inherit' }}>{money(w.low)}</td>
                  <td style={td}>{money(w.end)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)', lineHeight: 1.6, maxWidth: 820 }}>
        <strong>How this is worked out.</strong> Each day takes what actually crossed the Crab Place
        bank account on the same weekday a year ago, from QuickBooks, and scales it by how sales are
        running this year against last{p.factors ? ` (currently ${Math.round(p.factors.in * 100)}% on money in, ${Math.round(p.factors.out * 100)}% on money out)` : ''}.
        One-off money in last year (transfers, and any single deposit of $15,000 or more) is left out,
        because those were decisions and not something to expect again.{' '}
        <strong>How far to trust it:</strong> tested against 20 past four-week stretches, it was off by
        about {money(p.typicalMiss)} on average, in either direction. That is roughly one week of
        spending. So read it for which weeks are thin, not for the exact dollar. A buyer placing a
        normal order with a regular vendor is never held up by it; only spending beyond the usual is weighed.
      </div>
    </div>
  );
}
